import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import Select from 'react-select';
import { FiEdit2, FiSearch, FiPlus, FiX } from 'react-icons/fi';
import useDraftManager from '../../hooks/useDraftManager';
import GSTVerificationModal from '../../components/GSTVerificationModal';
import PANVerificationModal from '../../components/PANVerificationModal';

export default function TransporterManagement() {
  const { user } = useContext(AuthContext);

  const [transporters, setTransporters] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Pagination and search
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalTransporters, setTotalTransporters] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Notification states for draft auto-save
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Stations for dropdown
  const [stations, setStations] = useState([]);

  // Form fields
  const [form, setForm] = useState({
    name: '',
    phoneNumber: '',
    station: '',
    gstin: '',
    panNo: '',
    createdBy: user?.user?._id
  });

  // GST/PAN verification states
  const [isPanReadOnly, setIsPanReadOnly] = useState(false);
  const [isGstReadOnly, setIsGstReadOnly] = useState(false);
  const [showGstPanFields, setShowGstPanFields] = useState(false);
  const [showGSTVerification, setShowGSTVerification] = useState(false);
  const [showPANVerification, setShowPANVerification] = useState(false);

  // Draft Manager
  const draftManager = useDraftManager({
    featureType: 'transporterManagement',
    formData: isFormOpen ? form : null,
    setFormData: setForm,
    userId: user?.user?._id,
    companyId: user?.user?.company,
    onDraftLoaded: (draft) => {
      console.log('Transporter draft loaded:', draft);
      Swal.fire({
        icon: 'success',
        title: 'Draft Loaded',
        text: 'Your previous draft has been loaded',
        timer: 2000,
        showConfirmButton: false
      });
    },
    onDraftSaved: (draft, isManual) => {
      setNotificationMessage('Draft saved automatically');
      setShowNotification(true);
    },
    onError: (error) => {
      console.error('Transporter draft error:', error);
    }
  });

  // Sync dark mode with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setDarkMode(savedMode === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchTransporters();
  }, [page, limit, searchTerm]);

  useEffect(() => {
    fetchStations();
  }, []);

  // Fetch stations
  const fetchStations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/stations`);
      const stationOptions = response.data.map((st) => ({
        value: st._id,
        label: st.name
      }));
      setStations(stationOptions);
    } catch (err) {
      Swal.fire('Error', 'Failed to fetch stations', 'error');
    }
  };

  // Fetch transporters
  const fetchTransporters = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/transporters`, {
        params: {
          limit,
          page,
          search: searchTerm,
          userCompanyId: user.company
        }
      });
      setTransporters(response.data.transporters);
      setTotalTransporters(response.data.total);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch transporters', 'error');
    }
  };

  // Validate form
  const validateForm = () => {
    if (!form.name || !form.station) {
      Swal.fire('Error', 'Please fill all the fields', 'error');
      return false;
    }
    if (!form.phoneNumber || form.phoneNumber.length !== 10) {
      Swal.fire('Error', 'Phone number should be 10 digits', 'error');
      return false;
    }
    // Check if phone number contains only digits
    if (!/^\d{10}$/.test(form.phoneNumber)) {
      Swal.fire('Error', 'Phone number should contain only digits', 'error');
      return false;
    }
    return true;
  };

  // Add / Update
  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      if (isEditing) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/transporters/${editingId}`,
          form
        );
        Swal.fire('Success', 'Transporter updated successfully', 'success');
        
        // Delete draft after successful update
        if (draftManager.currentDraftId) {
          try {
            await draftManager.deleteDraft(draftManager.currentDraftId);
            console.log('✅ Draft deleted after transporter update');
          } catch (draftError) {
            console.error('⚠️ Failed to delete draft:', draftError);
          }
        }
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/transporters`, form);
        Swal.fire('Success', 'Transporter added successfully', 'success');
        
        // Delete draft after successful creation
        if (draftManager.currentDraftId) {
          try {
            await draftManager.deleteDraft(draftManager.currentDraftId);
            console.log('✅ Draft deleted after transporter creation');
          } catch (draftError) {
            console.error('⚠️ Failed to delete draft:', draftError);
          }
        }
      }
      fetchTransporters();
      resetForm();
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error submitting transporter:', error);
      const errorMessage = error.response?.data?.error || 'Failed to submit transporter';
      Swal.fire('Error', errorMessage, 'error');
    }
  };

  // Reset form
  const resetForm = () => {
    setForm({
      name: '',
      phoneNumber: '',
      station: '',
      gstin: '',
      panNo: '',
      createdBy: user?.user?._id
    });
    setIsEditing(false);
    setEditingId(null);
    setIsPanReadOnly(false);
    setIsGstReadOnly(false);
    setShowGstPanFields(false);
  };

  // Input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle phone number input - only allow digits
    if (name === 'phoneNumber') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        const processedValue = numericValue;
        setForm({ ...form, [name]: processedValue });
      }
      return;
    }
    
    // Convert transporter name to uppercase
    const processedValue = name === 'name' ? value.toUpperCase() : value;
    setForm({ ...form, [name]: processedValue });
  };

  // Station select
  const handleStationChange = (selectedOption) => {
    setForm({ ...form, station: selectedOption.value });
  };

  // GST/PAN Verification handlers
  const openGSTVerification = () => {
    setShowGSTVerification(true);
  };

  const handleGSTVerificationData = ({ gstData, gstin, useData }) => {
    if (useData && gstData) {
      const extractedData = gstData.extractedData || {};
      const extractedPAN = gstin.substring(2, 12);
      
      setForm(prevForm => ({
        ...prevForm,
        gstin: gstin,
        panNo: extractedPAN,
        name: extractedData.legalName || extractedData.tradeName || prevForm.name,
      }));
      
      setIsPanReadOnly(true);
      setIsGstReadOnly(true);
      setShowGstPanFields(true);
      
      const populatedFields = [];
      if (extractedData.legalName || extractedData.tradeName) populatedFields.push('Name');
      if (extractedPAN) populatedFields.push('PAN');
      
      Swal.fire({
        title: 'GST Verified Successfully!',
        html: `
          <div class="text-left">
            <p class="mb-2"><strong>GSTIN:</strong> ${gstin}</p>
            <p class="mb-2"><strong>Extracted PAN:</strong> ${extractedPAN}</p>
            <p class="mb-2"><strong>Status:</strong> ${gstData.sts || 'N/A'}</p>
            <p class="mb-2"><strong>Constitution:</strong> ${gstData.ctb || 'N/A'}</p>
            <p class="mb-2"><strong>Registration Date:</strong> ${gstData.rgdt || 'N/A'}</p>
            ${populatedFields.length > 0 ? `<p class="mt-3 text-sm text-green-600"><strong>Auto-filled:</strong> ${populatedFields.join(', ')}</p>` : ''}
            <p class="mt-3 text-sm text-blue-600">ℹ️ PAN has been automatically extracted from GSTIN. No separate PAN verification needed.</p>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3b82f6'
      });
    }
  };

  const handlePANVerificationData = ({ panNo, verificationData }) => {
    setForm(prevForm => ({ ...prevForm, panNo: panNo }));
    setIsPanReadOnly(true);
    setShowGstPanFields(true);
    
    const gstinList = verificationData.gstinResList || [];
    
    if (gstinList.length > 0) {
      const gstinOptions = gstinList.map(item => 
        `<option value="${item.gstin}">${item.gstin} (${item.authStatus || 'N/A'} - State: ${item.stateCd || 'N/A'})</option>`
      ).join('');
      
      Swal.fire({
        icon: 'success',
        title: 'PAN Verified Successfully!',
        html: `
          <div class="text-left">
            <p class="mb-3"><strong>PAN:</strong> ${panNo}</p>
            <p class="mb-2 font-semibold">This PAN has ${gstinList.length} linked GSTIN(s):</p>
            <select id="gstin-select" class="w-full border border-gray-300 rounded-md p-2 mb-3">
              <option value="">-- Select a GSTIN --</option>
              ${gstinOptions}
            </select>
            <p class="text-sm text-blue-600">💡 Select a GSTIN to auto-fill GST field</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Use Selected GSTIN',
        cancelButtonText: 'Skip',
        confirmButtonColor: '#3b82f6',
        cancelButtonColor: '#6b7280',
        preConfirm: () => {
          const selectedGSTIN = document.getElementById('gstin-select').value;
          if (!selectedGSTIN) {
            Swal.showValidationMessage('Please select a GSTIN');
            return false;
          }
          return selectedGSTIN;
        }
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          setForm(prevForm => ({ ...prevForm, gstin: result.value }));
          setIsGstReadOnly(true);
          Swal.fire({
            icon: 'success',
            title: 'GSTIN Set Successfully!',
            html: `<div class="text-left">
                <p class="mb-2"><strong>Selected GSTIN:</strong> ${result.value}</p>
                <p class="text-sm text-green-600 mt-3">✓ GSTIN has been set from PAN verification.</p>
              </div>`,
            timer: 3000, showConfirmButton: false
          });
        }
      });
    } else {
      Swal.fire({
        icon: 'success',
        title: 'PAN Verified Successfully!',
        html: `<div class="text-left">
            <p class="mb-2"><strong>PAN:</strong> ${panNo}</p>
            <p class="text-sm text-yellow-600 mt-3">⚠️ No linked GSTINs found for this PAN.</p>
          </div>`,
        timer: 3000, showConfirmButton: false
      });
    }
  };

  const handleClearGST = () => {
    setForm(prevForm => ({ ...prevForm, gstin: '', panNo: '' }));
    setIsPanReadOnly(false);
    setIsGstReadOnly(false);
    setShowGstPanFields(false);
  };

  // Pagination
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const totalPages = Math.ceil(totalTransporters / limit);

  // Enhanced pagination helpers
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
      range.push(i);
    }

    if (page - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (page + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  // Helper function for dark mode select styles
  const getSelectStyles = (isDark) => ({
    control: (base, state) => ({
      ...base,
      backgroundColor: isDark ? '#374151' : '#ffffff',
      borderColor: state.isFocused ? '#3B82F6' : (isDark ? '#4B5563' : '#E5E7EB'),
      borderWidth: state.isFocused ? '2px' : '1px',
      borderRadius: '12px',
      padding: '8px',
      color: isDark ? '#F3F4F6' : '#111827',
      boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : 'none',
      '&:hover': {
        borderColor: isDark ? '#6B7280' : '#D1D5DB'
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#111827'
    }),
    input: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#111827'
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? '#374151' : '#ffffff',
      border: isDark ? '1px solid #4B5563' : '1px solid #E5E7EB',
      borderRadius: '12px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#3B82F6' 
        : state.isFocused 
        ? (isDark ? '#4B5563' : '#EFF6FF')
        : (isDark ? '#374151' : '#ffffff'),
      color: state.isSelected ? '#ffffff' : (isDark ? '#F3F4F6' : '#374151'),
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: state.isSelected ? '#3B82F6' : (isDark ? '#4B5563' : '#EFF6FF')
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? '#9CA3AF' : '#6B7280'
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: isDark ? '#9CA3AF' : '#6B7280',
      '&:hover': {
        color: isDark ? '#D1D5DB' : '#374151'
      }
    })
  });

  return (
    <div className={`min-h-screen p-3 sm:p-6 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-blue-50'}`}>
      <div className="w-full mx-auto">
        {/* Header Section */}
        <div className={`rounded-2xl shadow-xl p-6 mb-8 border-l-4 border-blue-500 ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-6 lg:mb-0">
              <h1 className={`text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2`}>
                Transporter Management
              </h1>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Manage and organize your transportation partners
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative group">
                <FiSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 transition-colors ${
                  darkMode ? 'text-gray-500 group-focus-within:text-blue-400' : 'text-gray-400 group-focus-within:text-blue-500'
                }`} />
                <input
                  type="text"
                  placeholder="Search transporters..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all w-full sm:w-72 shadow-sm hover:shadow-md ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' 
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
              <button
                onClick={() => setIsFormOpen(true)}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
              >
                <FiPlus className="text-lg" /> Add New Transporter
              </button>
            </div>
          </div>
        </div>

        {/* Form Modal */}
        {isFormOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className={`rounded-2xl shadow-2xl w-full max-w-md transform animate-in slide-in-from-bottom duration-300 ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className={`flex justify-between items-center p-6 border-b ${
                darkMode ? 'border-gray-700' : 'border-gray-100'
              }`}>
                <div>
                  <h2 className={`text-2xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    {isEditing ? 'Edit Transporter' : 'Add New Transporter'}
                  </h2>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {isEditing ? 'Update transporter information' : 'Enter transporter details below'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setIsFormOpen(false);
                      resetForm();
                    }}
                    className={`p-2 rounded-full transition-colors ${
                      darkMode 
                        ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    }`}>
                    <FiX size={24} />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-5">
                <div className="space-y-2">
                  <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Name *
                  </label>
                  <input
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleInputChange}
                    placeholder="Enter transporter name (will be converted to uppercase)"
                    className={`w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all uppercase ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 hover:border-gray-500' 
                        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500 hover:border-gray-300'
                    }`}
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <div className="space-y-2">
                  <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Phone Number *
                  </label>
                  <input
                    name="phoneNumber"
                    type="tel"
                    value={form.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="Enter 10-digit phone number"
                    maxLength="10"
                    pattern="[0-9]{10}"
                    className={`w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 hover:border-gray-500' 
                        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500 hover:border-gray-300'
                    }`}
                  />
                </div>
                <div className="space-y-2">
                  <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Station *
                  </label>
                  <Select
                    options={stations}
                    value={stations.find((opt) => opt.value === form.station)}
                    onChange={handleStationChange}
                    placeholder="Select station"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={getSelectStyles(darkMode)}
                  />
                </div>
                {/* GST/PAN Verification Section */}
                {!showGstPanFields ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex flex-col">
                      <label className={`block text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        GST Verification
                      </label>
                      <button
                        type="button"
                        onClick={openGSTVerification}
                        className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-xl transition-all font-medium ${
                          darkMode 
                            ? 'border-blue-700 text-blue-400 hover:bg-blue-900/30 hover:border-blue-500' 
                            : 'border-blue-300 text-blue-600 hover:bg-blue-50 hover:border-blue-500'
                        }`}
                      >
                        <span>🔍</span>
                        <span>Verify GST Number</span>
                      </button>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Verify GSTIN and auto-extract PAN
                      </p>
                    </div>
                    <div className="flex flex-col">
                      <label className={`block text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        PAN Verification
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowPANVerification(true)}
                        className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-xl transition-all font-medium ${
                          darkMode 
                            ? 'border-green-700 text-green-400 hover:bg-green-900/30 hover:border-green-500' 
                            : 'border-green-300 text-green-600 hover:bg-green-50 hover:border-green-500'
                        }`}
                      >
                        <span>🔍</span>
                        <span>Verify PAN Number</span>
                      </button>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Verify PAN number independently
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* GST Field with Verify/Clear */}
                    <div className="space-y-2">
                      <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        GSTIN {isGstReadOnly && <span className="text-xs text-green-500 ml-1">(Verified ✅)</span>}
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <input
                            name="gstin"
                            type="text"
                            value={form.gstin}
                            onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                            disabled={isGstReadOnly}
                            maxLength="15"
                            placeholder="Enter GSTIN"
                            className={`w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                              isGstReadOnly ? 'opacity-75 cursor-not-allowed' : ''
                            } ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' 
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                            }`}
                          />
                          {form.gstin && (
                            <button
                              type="button"
                              onClick={handleClearGST}
                              className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full transition-colors ${
                                darkMode ? 'text-gray-400 hover:text-red-400 hover:bg-gray-600' : 'text-gray-400 hover:text-red-500 hover:bg-gray-100'
                              }`}
                              title="Clear GST & PAN"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={openGSTVerification}
                          disabled={isGstReadOnly}
                          className={`px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                            isGstReadOnly
                              ? darkMode ? 'bg-green-900/30 text-green-400 cursor-not-allowed' : 'bg-green-50 text-green-600 cursor-not-allowed'
                              : darkMode ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-800/50' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                          }`}
                        >
                          {isGstReadOnly ? '✅ Verified' : '🔍 Verify'}
                        </button>
                      </div>
                    </div>

                    {/* PAN Field with Verify */}
                    <div className="space-y-2">
                      <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        PAN No {isPanReadOnly && <span className="text-xs text-green-500 ml-1">(Auto-extracted from GST ✅)</span>}
                      </label>
                      <div className="flex gap-2">
                        <input
                          name="panNo"
                          type="text"
                          value={form.panNo}
                          onChange={(e) => setForm({ ...form, panNo: e.target.value.toUpperCase() })}
                          disabled={isPanReadOnly}
                          maxLength="10"
                          placeholder="Enter PAN Number"
                          className={`flex-1 border rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                            isPanReadOnly ? 'opacity-75 cursor-not-allowed' : ''
                          } ${
                            darkMode 
                              ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' 
                              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPANVerification(true)}
                          disabled={isPanReadOnly}
                          className={`px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                            isPanReadOnly
                              ? darkMode ? 'bg-green-900/30 text-green-400 cursor-not-allowed' : 'bg-green-50 text-green-600 cursor-not-allowed'
                              : darkMode ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-800/50' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                          }`}
                        >
                          {isPanReadOnly ? '✅ From GST' : '🔍 Verify'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
                <div className={`flex gap-3 pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                  <button
                    onClick={() => {
                      setIsFormOpen(false);
                      resetForm();
                    }}
                    className={`flex-1 px-4 py-3 rounded-xl transition-colors font-medium ${
                      darkMode 
                        ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-700' 
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    {isEditing ? 'Update' : 'Add'} Transporter
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transporters Table */}
        <div className={`rounded-2xl shadow-xl overflow-hidden border ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
        }`}>
          <div className={`px-6 py-4 border-b ${
            darkMode ? 'bg-gray-750 border-gray-700' : 'bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold flex items-center gap-2 ${
              darkMode ? 'text-gray-100' : 'text-gray-800'
            }`}>
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              Transporters List
              <span className={`text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                ({totalTransporters} total)
              </span>
            </h3>
          </div>

          {/* Desktop Table View - Hidden on Mobile */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={darkMode ? 'bg-gray-750' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-3 py-4 text-left text-xs font-bold uppercase tracking-wider w-20 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>ID</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Name</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Phone</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Station</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>GSTIN</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>PAN</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider w-20 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${
                darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-100'
              }`}>
                {transporters.map((transporter, index) => (
                  <tr key={transporter._id} className={`transition-colors group ${
                    darkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                  }`}>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span className={`text-xs font-mono px-2 py-1 rounded-md ${
                        darkMode ? 'text-gray-300 bg-gray-700' : 'text-gray-600 bg-gray-100'
                      }`}>
                        {transporter.id}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                        {transporter.name}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {transporter.phoneNumber}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium truncate max-w-24 ${
                        darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {transporter.station?.name || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-sm font-mono truncate max-w-32 block ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {transporter.gstin || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-sm font-mono truncate max-w-32 block ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {transporter.panNo || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={async () => {
                          // Delete any existing draft when editing a transporter
                          if (draftManager.currentDraftId) {
                            try {
                              await draftManager.deleteDraft(draftManager.currentDraftId);
                              console.log('✅ Draft deleted when editing transporter');
                            } catch (draftError) {
                              console.error('⚠️ Failed to delete draft on edit:', draftError);
                              // Don't block user flow if draft deletion fails
                            }
                          }
                          
                          setIsEditing(true);
                          setEditingId(transporter._id);
                          setForm({
                            name: transporter.name,
                            phoneNumber: transporter.phoneNumber,
                            station: transporter.station?._id,
                            gstin: transporter.gstin || '',
                            panNo: transporter.panNo || '',
                            createdBy: user?.user?._id
                          });
                          // Set verification state based on existing data
                          if (transporter.gstin?.trim() && transporter.gstin.length === 15 && transporter.panNo?.trim()) {
                            const extractedPAN = transporter.gstin.substring(2, 12);
                            if (transporter.panNo === extractedPAN) {
                              setIsPanReadOnly(true);
                              setIsGstReadOnly(true);
                              setShowGstPanFields(true);
                            } else {
                              setIsPanReadOnly(false);
                              setIsGstReadOnly(false);
                              setShowGstPanFields(transporter.gstin?.trim() || transporter.panNo?.trim() ? true : false);
                            }
                          } else {
                            setIsPanReadOnly(false);
                            setIsGstReadOnly(false);
                            setShowGstPanFields(transporter.gstin?.trim() || transporter.panNo?.trim() ? true : false);
                          }
                          setIsFormOpen(true);
                        }}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all group-hover:shadow-md ${
                          darkMode 
                            ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-900' 
                            : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                        }`}
                      >
                        <FiEdit2 size={14} />
                        <span>Edit</span>
                      </button>
                    </td>
                  </tr>
                ))}
                {transporters.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-100'
                        }`}>
                          <FiSearch className={`text-2xl ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        </div>
                        <p className={`text-lg font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          No transporters found
                        </p>
                        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          Try adjusting your search terms
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View - Hidden on Desktop */}
          <div className="md:hidden">
            {transporters.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {transporters.map((transporter, index) => (
                  <div 
                    key={transporter._id} 
                    className={`p-4 transition-colors ${
                      darkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                            darkMode ? 'text-gray-300 bg-gray-700' : 'text-gray-600 bg-gray-100'
                          }`}>
                            {transporter.id}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {transporter.station?.name || 'N/A'}
                          </span>
                        </div>
                        <h3 className={`text-base font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                          {transporter.name}
                        </h3>
                      </div>
                      <button
                        onClick={() => {
                          setIsEditing(true);
                          setEditingId(transporter._id);
                          setForm({
                            name: transporter.name,
                            phoneNumber: transporter.phoneNumber,
                            station: transporter.station?._id,
                            gstin: transporter.gstin || '',
                            panNo: transporter.panNo || '',
                            createdBy: user?.user?._id
                          });
                          // Set verification state based on existing data
                          if (transporter.gstin?.trim() && transporter.gstin.length === 15 && transporter.panNo?.trim()) {
                            const extractedPAN = transporter.gstin.substring(2, 12);
                            if (transporter.panNo === extractedPAN) {
                              setIsPanReadOnly(true);
                              setIsGstReadOnly(true);
                              setShowGstPanFields(true);
                            } else {
                              setIsPanReadOnly(false);
                              setIsGstReadOnly(false);
                              setShowGstPanFields(transporter.gstin?.trim() || transporter.panNo?.trim() ? true : false);
                            }
                          } else {
                            setIsPanReadOnly(false);
                            setIsGstReadOnly(false);
                            setShowGstPanFields(transporter.gstin?.trim() || transporter.panNo?.trim() ? true : false);
                          }
                          setIsFormOpen(true);
                        }}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all shadow-sm ${
                          darkMode 
                            ? 'text-blue-400 bg-blue-900 hover:bg-blue-800' 
                            : 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                        }`}
                      >
                        <FiEdit2 size={14} />
                        <span className="text-sm font-medium">Edit</span>
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <svg className={`w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {transporter.phoneNumber}
                        </span>
                      </div>
                      
                      {transporter.gstin && (
                        <div className="flex items-center gap-2">
                          <svg className={`w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className={`text-sm font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            GST: {transporter.gstin}
                          </span>
                        </div>
                      )}
                      {transporter.panNo && (
                        <div className="flex items-center gap-2">
                          <svg className={`w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0" />
                          </svg>
                          <span className={`text-sm font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            PAN: {transporter.panNo}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-12 text-center">
                <div className="flex flex-col items-center justify-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                    darkMode ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                    <FiSearch className={`text-2xl ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <p className={`text-lg font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No transporters found
                  </p>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Try adjusting your search terms
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`rounded-2xl shadow-lg border mt-6 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
          }`}>
            <div className={`px-4 sm:px-6 py-3 sm:py-4 border-t ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="hidden sm:inline">Showing </span>
                    <span className="font-medium">{((page - 1) * limit) + 1}</span>
                    <span className="hidden sm:inline"> to </span>
                    <span className="sm:hidden">-</span>
                    <span className="font-medium">{Math.min(page * limit, totalTransporters)}</span>
                    <span className="hidden sm:inline"> of </span>
                    <span className="sm:hidden">/</span>
                    <span className="font-medium">{totalTransporters}</span>
                    <span className="hidden sm:inline"> results</span>
                  </p>
                  <select
                    value={limit}
                    onChange={(e) => {
                      setLimit(parseInt(e.target.value));
                      setPage(1);
                    }}
                    className={`px-2 sm:px-3 py-1 border rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                
                <nav className="flex items-center justify-center sm:justify-start space-x-1">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className={`relative inline-flex items-center px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium border rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      darkMode 
                        ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                        : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </button>
                  
                  <div className="hidden sm:flex items-center space-x-1">
                    {getPageNumbers().map((pageNum, index) => (
                      pageNum === '...' ? (
                        <span key={index} className={`px-3 py-1 text-sm ${
                          darkMode ? 'text-gray-500' : 'text-gray-500'
                        }`}>...</span>
                      ) : (
                        <button
                          key={index}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-3 py-1 text-sm font-medium border rounded-md transition-all ${
                            pageNum === page
                              ? darkMode 
                                ? 'z-10 bg-blue-500/20 border-blue-500 text-blue-400' 
                                : 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : darkMode 
                                ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' 
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    ))}
                  </div>
                  
                  {/* Mobile: Show only current page */}
                  <div className="sm:hidden flex items-center">
                    <span className={`px-2 py-1 text-xs font-medium ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {page} / {totalPages}
                    </span>
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className={`relative inline-flex items-center px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium border rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      darkMode 
                        ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                        : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Auto-save Notification */}
      {(draftManager.autoSaving || showNotification) && (
        <div
          className={`fixed bottom-6 right-6 bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-4 flex items-center gap-4 max-w-sm border transition-all duration-300 z-50 ${
              draftManager.autoSaving 
                ? 'border-blue-300 dark:border-blue-700' 
                : 'border-green-300 dark:border-green-700'
            }`}
            onClick={() => !draftManager.autoSaving && setShowNotification(false)}
            title={!draftManager.autoSaving ? 'Click to dismiss' : ''}
            style={{ cursor: draftManager.autoSaving ? 'default' : 'pointer' }}
          >
              <div className={`text-2xl ${draftManager.autoSaving ? 'animate-spin' : ''}`}>
                {draftManager.autoSaving ? '🔄' : '✅'}
              </div>
              <div className="flex-1">
                <p className={`font-semibold text-sm ${
                  draftManager.autoSaving 
                    ? 'text-blue-700 dark:text-blue-400' 
                    : 'text-green-700 dark:text-green-400'
                }`}>
                  {draftManager.autoSaving ? 'Auto-saving...' : notificationMessage}
                </p>
                {draftManager.lastSaved && !draftManager.autoSaving && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {draftManager.lastSaved.toLocaleTimeString('en-IN')}
                  </p>
                )}
              </div>
          </div>
      )}

      {/* GST Verification Modal */}
      <GSTVerificationModal
        isOpen={showGSTVerification}
        onClose={() => setShowGSTVerification(false)}
        onDataReceived={handleGSTVerificationData}
        initialGSTIN={form.gstin}
      />

      {/* PAN Verification Modal */}
      <PANVerificationModal
        isOpen={showPANVerification}
        onClose={() => setShowPANVerification(false)}
        onDataReceived={handlePANVerificationData}
        initialPAN={form.panNo}
      />
    </div>
  );
}
